import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Tracks the floating create button so the scroll-to-top button can sit right
 * above it when collapsed and step up out of the way when its actions expand.
 */
export type CreateFabState = 'absent' | 'collapsed' | 'expanded';

type CreateFabContextValue = {
  fabState: CreateFabState;
  setFabState: (state: CreateFabState) => void;
};

const CreateFabContext = createContext<CreateFabContextValue | null>(null);

export function CreateFabProvider({ children }: { children: ReactNode }) {
  const [fabState, setFabState] = useState<CreateFabState>('absent');
  const value = useMemo(() => ({ fabState, setFabState }), [fabState]);

  return <CreateFabContext.Provider value={value}>{children}</CreateFabContext.Provider>;
}

export function useCreateFabState(): CreateFabState {
  return useContext(CreateFabContext)?.fabState ?? 'absent';
}

/** Reports the create button's presence and expansion to the surrounding shell. */
export function useReportCreateFab(expanded: boolean) {
  const setFabState = useContext(CreateFabContext)?.setFabState;

  useEffect(() => {
    setFabState?.(expanded ? 'expanded' : 'collapsed');
  }, [expanded, setFabState]);

  useEffect(() => () => setFabState?.('absent'), [setFabState]);
}
